import React, { useEffect, useState } from 'react';
import { Home, PlayCircle, Trash2, Pencil, Plus } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { useAdminRole } from '../hooks/useAdminRole.ts';
import {
  CamaraStreamVideo,
  createCamaraStreamVideo,
  deleteCamaraStreamVideo,
  getCamaraStreamVideos,
  updateCamaraStreamVideo,
} from '../services/camaraStreamService.ts';

const emptyForm = {
  youtube_id: '',
  title: '',
  instructor: '',
  duration: '',
  level: 'Inicial' as CamaraStreamVideo['level'],
  category: 'Ventas' as CamaraStreamVideo['category'],
  activo: true,
};

export default function CamaraStreamAdmin() {
  const { isAdmin } = useAdminRole();
  const [videos, setVideos] = useState<CamaraStreamVideo[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);

  const load = async () => setVideos(await getCamaraStreamVideos());
  useEffect(() => {
    load();
  }, []);

  if (!isAdmin) return <Navigate to="/inicio" replace />;

  const openCreateModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowFormModal(true);
  };

  const openEditModal = (v: CamaraStreamVideo) => {
    setEditingId(v.video_id);
    setForm({ ...v, youtube_id: v.youtube_id });
    setShowFormModal(true);
  };

  const guardar = async () => {
    if (!form.youtube_id || !form.title.trim()) return;
    if (editingId) {
      await updateCamaraStreamVideo(editingId, form);
    } else {
      await createCamaraStreamVideo(form);
    }
    setForm(emptyForm);
    setEditingId(null);
    setShowFormModal(false);
    await load();
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4">
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/inicio" className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800">
              <Home className="mr-2 h-4 w-4" /> Inicio
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <PlayCircle className="h-4 w-4 text-gray-400" />
              <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Admin Cámara Stream</span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="rounded-xl bg-white p-4 shadow flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">ABM de videos · Cámara Stream</h1>
          <p className="text-sm text-slate-600">Gestioná contenido en formato tarjetas.</p>
        </div>
        <button className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-white" onClick={openCreateModal}>
          <Plus className="h-4 w-4" /> Crear video
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {videos.map((v) => (
          <article key={v.video_id} className="overflow-hidden rounded-2xl border border-sky-100 bg-white shadow-sm">
            <div className="aspect-video w-full bg-slate-100">
              <iframe
                className="h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/${v.youtube_id}`}
                title={v.title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="space-y-2 p-3">
              <h3 className="line-clamp-2 text-sm font-bold text-slate-900">{v.title}</h3>
              <p className="text-xs text-slate-600">{v.instructor || '—'} · {v.duration || '—'}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">{v.category}</span>
                <span className={`rounded-full px-2 py-1 font-semibold ${v.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
                  {v.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="flex gap-2 pt-1">
                <button className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs" onClick={() => openEditModal(v)}>
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </button>
                <button className="inline-flex items-center gap-1 rounded border border-rose-200 px-2 py-1 text-xs text-rose-600" onClick={async () => { await deleteCamaraStreamVideo(v.video_id); await load(); }}>
                  <Trash2 className="h-3.5 w-3.5" /> Eliminar
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-4 shadow-xl space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editingId ? 'Editar video' : 'Crear video'}</h3>
              <button className="rounded border px-3 py-1" onClick={() => { setShowFormModal(false); setEditingId(null); setForm(emptyForm); }}>Cerrar</button>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <input className="rounded border p-2" placeholder="YouTube ID" value={form.youtube_id} onChange={(e) => setForm({ ...form, youtube_id: e.target.value.trim() })} />
              <input className="rounded border p-2" placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <input className="rounded border p-2" placeholder="Instructor" value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} />
              <input className="rounded border p-2" placeholder="Duración ej. 12:40" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
              <select className="rounded border p-2" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value as any })}>
                <option>Inicial</option><option>Intermedio</option><option>Avanzado</option>
              </select>
              <select className="rounded border p-2" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as any })}>
                <option>Ventas</option><option>Marketing</option><option>Gestión</option><option>IA</option>
              </select>
              <label className="md:col-span-2 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} /> Activo
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button className="rounded border px-3 py-2" onClick={() => { setShowFormModal(false); setEditingId(null); setForm(emptyForm); }}>Cancelar</button>
              <button className="rounded bg-blue-600 px-3 py-2 text-white" onClick={guardar}>{editingId ? 'Guardar cambios' : 'Crear video'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
